// @flow

import React from 'react';
import styled from 'styled-components';

const StyledValidatedInput = styled.div.attrs({
  style: ({ marginBottom }) => ({
    marginBottom: marginBottom
  })
})`
  position: relative;
`;

export const Input = styled.input.attrs({
  maxLength: props => props.maxLength,
  style: ({ fontSize }) => ({
    fontSize: fontSize
  })
})`
  display: block;
  height: 35px;
  width: 100%;
  border-radius: 2px;
  background-color: #ffffff;
  padding: 0 0 0 15px;
  border: 1px solid #ececec;
  line-height: 35px;
  font-weight: ${({ weight }) => weight};
  color: #4a4a4a;
  &::placeholder {
    font-weight: 400;
    color: #9b9b9b;
  }
  &:disabled {
    background: #ececec;
    color: #9b9b9b;
  }
  ${props => props.hasError && 'border-color: #dc3545;'}
  &::-webkit-outer-spin-button,::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &[type='number'] {
    -moz-appearance: textfield;
  }
`;

const ErrorMessage = styled.span.attrs({
  className: 'error-message'
})`
  color: #ff1400;
  top: 35px;
  font-size: 14px;
  line-height: 17px;
  margin-top: 0px;
  position: initial;
`;

type PropsType = {
  name: string,
  value: any,
  type: string,
  placeholder: string,
  onValueChange: (value: string, input: string) => void,
  fontSize?: number,
  schema?: any,
  children?: any,
  maxLength?: number,
  readOnly?: boolean,
  allowComponentUpdate?: boolean,
  marginBottom?: number,
  disabled?: boolean,
  weight?: 'normal' | 'bold',
  minNumber?: number,
  onFocus?: (input: string) => void,
  className?: string,
  suppressDerivedStateFromProps?: boolean
};

type StateType = {
  error: string,
  touched: boolean,
  inputValue: any
};

export default class SimpleInputComponent extends React.Component<
  PropsType,
  StateType
> {
  static defaultProps = {
    fontSize: 13,
    readOnly: false,
    allowComponentUpdate: false,
    marginBottom: 15,
    weight: 'bold',
    suppressDerivedStateFromProps: false
  };
  state = {
    error: '',
    touched: false,
    inputValue: this.props.value
  };

  componentDidUpdate(prevProps: PropsType) {
    if (
      this.props.value !== prevProps.value &&
      this.props.allowComponentUpdate
    ) {
      this.setState({
        inputValue: this.props.value,
        error: '',
        touched: false
      });
    }
  }

  handleChange = ({ target }: Event<HTMLInputElement>): void => {
    let inputValue = target.value;

    if (this.props.type === 'number' && this.props.minNumber !== undefined) {
      inputValue = +inputValue;

      if (!inputValue || inputValue < this.props.minNumber) {
        inputValue = `${this.props.minNumber}`;
      }
    }

    this.setState({ inputValue }, () => {
      this.validationSchema(inputValue, false, false);
    });
  };

  handleBlur = () => {
    this.validationSchema(this.state.inputValue, true, !this.state.error);
    if (!this.state.touched) this.setState({ touched: true });
  };

  handleFocus = () => {
    const { name } = this.props;
    this.props.onFocus && this.props.onFocus(name);
  };

  validationSchema = (
    inputValue: any,
    onBlur: boolean,
    error: boolean
  ): void => {
    const { name } = this.props;
    if (this.props.schema) {
      this.props.schema
        .validate({ inputValue })
        .then(() => {
          this.setState({ error: '' });
        })
        .catch(e => {
          this.setState({ error: e.message });
        });
    }
    this.props.onValueChange && this.props.onValueChange(inputValue, name);
  };

  static getDerivedStateFromProps(nextProps: PropsType, prevState: StateType) {
    // FIXME: Preserve until move to functional component
    if (nextProps.suppressDerivedStateFromProps) return null;

    if (nextProps.value !== prevState.inputValue) {
      return {
        inputValue: nextProps.value
      };
    }
    return null;
  }

  render() {
    const { error, touched, inputValue } = this.state;
    const {
      readOnly,
      placeholder,
      fontSize,
      type,
      maxLength,
      marginBottom,
      disabled,
      weight,
      className
    } = this.props;
    return (
      <StyledValidatedInput marginBottom={marginBottom} className={className}>
        <Input
          readOnly={readOnly}
          fontSize={fontSize}
          defaultValue={inputValue}
          type={type}
          placeholder={placeholder}
          onChange={this.handleChange}
          onBlur={this.handleBlur}
          onFocus={this.handleFocus}
          maxLength={maxLength}
          hasError={error && touched}
          disabled={disabled}
          weight={weight}
        />
        {this.props.children}
        {error && touched && <ErrorMessage>{error}</ErrorMessage>}
      </StyledValidatedInput>
    );
  }
}

export const InputComponentWithoutDerivedState = (props: any) => (
  <SimpleInputComponent {...props} suppressDerivedStateFromProps />
);
